import { Link, useSearchParams } from 'react-router-dom'
import SEO from '../components/SEO'
import { TopNav, Footer } from '../components/PublicLayout'
import {
  CATEGORIES,
  getArticlesSorted,
  getCategoryLabel,
  shouldShowCategoryMenu,
  getCategoryCounts,
  getArticlePrimaryCategory,
} from '../articles/index'

// ArticlesIndex
// -------------
// Lists every published article newest-first. Each card links to
// /articles/<slug>.
//
// Category filter is via the ?cat=<slug> query parameter. The sidebar
// is only rendered once ARTICLES.length >= CATEGORY_MENU_MIN_ARTICLES
// (currently 10). Below that threshold, the page is a single flat list
// with no sidebar at all -- this avoids the "mostly empty categories"
// UX problem when launching with few articles. Once the threshold is
// crossed, the sidebar appears automatically; no code change required.

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

export default function ArticlesIndex() {
  const [searchParams] = useSearchParams()
  const selectedCategory = searchParams.get('cat')
  const articles         = getArticlesSorted(selectedCategory)
  const showMenu         = shouldShowCategoryMenu()
  const counts           = showMenu ? getCategoryCounts() : null

  // Categories with at least one article. Hides empty categories from
  // the menu so a "Payroll (0)" entry never appears.
  const visibleCategories = showMenu
    ? CATEGORIES.filter(c => (counts[c.slug] || 0) > 0)
    : []

  const categoryLabel = selectedCategory ? getCategoryLabel(selectedCategory) : null

  // Page-level SEO copy adapts when a category is selected so the
  // category landing page has its own distinct title and description.
  // Helps avoid duplicate-content penalties and gives each category
  // page its own chance to rank.
  const pageTitle = categoryLabel
    ? `${categoryLabel} articles for UK bookkeepers`
    : 'Articles for UK bookkeepers'
  const pageDescription = categoryLabel
    ? `${categoryLabel} guides written for working UK bookkeepers by the Receipt Chaser team.`
    : 'Guides on UK bookkeeping, VAT, payroll, and small business accounting. Written for working bookkeepers by the Receipt Chaser team.'
  const pagePath = categoryLabel
    ? `/articles?cat=${selectedCategory}`
    : '/articles'

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO title={pageTitle} description={pageDescription} path={pagePath} />
      <TopNav />

      {/* ─── Header ─────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 0 48px' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 720, textAlign: 'center' }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 16,
          }}>
            Articles
          </p>
          <h1 style={{
            marginBottom: 24, lineHeight: 1.15,
            fontSize: 'clamp(2rem, 5vw, 2.8rem)',
          }}>
            {categoryLabel
              ? <>Articles in <span style={{ color: 'var(--accent)' }}>{categoryLabel}</span></>
              : <>Practical guides for<br/>UK bookkeepers.</>}
          </h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '1.05rem', lineHeight: 1.6, margin: 0 }}>
            Written by working bookkeepers. No filler, no sales talk,
            just the reference material we wish we'd had when we started.
          </p>
        </div>
      </section>

      {/* ─── Body: sidebar + list, or just list ─────────────────────── */}
      <section style={{ padding: '0 0 96px' }}>
        <div className="container" style={{
          padding: '0 24px',
          maxWidth: showMenu ? 1080 : 760,
          display: showMenu ? 'grid' : 'block',
          gridTemplateColumns: showMenu ? '220px 1fr' : undefined,
          gap: showMenu ? 48 : undefined,
          alignItems: 'start',
        }}>
          {showMenu && (
            <aside>
              <div style={{
                fontSize: '0.74rem', fontWeight: 700,
                color: 'var(--ink)', textTransform: 'uppercase',
                letterSpacing: '0.08em', marginBottom: 16,
                paddingBottom: 12, borderBottom: '1px solid var(--surface-2)',
              }}>
                Categories
              </div>
              <ul style={{
                listStyle: 'none', padding: 0, margin: 0,
                display: 'flex', flexDirection: 'column', gap: 4,
              }}>
                <li>
                  <Link
                    to="/articles"
                    style={{
                      display: 'block', padding: '10px 14px', borderRadius: 8,
                      textDecoration: 'none',
                      fontSize: '0.92rem',
                      color: !selectedCategory ? 'var(--accent)' : 'var(--ink-muted)',
                      background: !selectedCategory ? 'var(--surface-2)' : 'transparent',
                      fontWeight: !selectedCategory ? 600 : 400,
                    }}
                  >
                    All articles
                  </Link>
                </li>
                {visibleCategories.map(cat => {
                  const active = selectedCategory === cat.slug
                  return (
                    <li key={cat.slug}>
                      <Link
                        to={`/articles?cat=${cat.slug}`}
                        style={{
                          display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', padding: '10px 14px',
                          borderRadius: 8, textDecoration: 'none',
                          fontSize: '0.92rem',
                          color: active ? 'var(--accent)' : 'var(--ink-muted)',
                          background: active ? 'var(--surface-2)' : 'transparent',
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        <span>{cat.label}</span>
                        <span style={{
                          fontSize: '0.78rem',
                          color: active ? 'var(--accent)' : 'var(--ink-faint)',
                        }}>
                          {counts[cat.slug]}
                        </span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </aside>
          )}

          <div>
            {articles.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>
                No articles in this category yet.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
                {articles.map(article => (
                  <li key={article.slug}>
                    <Link
                      to={`/articles/${article.slug}`}
                      style={{
                        display: 'block',
                        padding: '28px 32px',
                        background: 'var(--white)',
                        borderRadius: 14,
                        border: '1px solid var(--surface-2)',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'border-color 0.15s, box-shadow 0.15s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = 'var(--accent)'
                        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = 'var(--surface-2)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      <div style={{
                        display: 'flex', gap: 12, alignItems: 'center',
                        fontSize: '0.78rem', color: 'var(--ink-muted)',
                        marginBottom: 10, letterSpacing: '0.02em',
                      }}>
                        <span>{formatDate(article.date)}</span>
                        {getCategoryLabel(getArticlePrimaryCategory(article)) && (
                          <>
                            <span style={{ color: 'var(--ink-faint)' }}>·</span>
                            <span style={{
                              color: 'var(--accent)', fontWeight: 600,
                              textTransform: 'uppercase', fontSize: '0.72rem',
                              letterSpacing: '0.05em',
                            }}>
                              {getCategoryLabel(getArticlePrimaryCategory(article))}
                            </span>
                          </>
                        )}
                      </div>
                      <h2 style={{
                        fontSize: '1.4rem', margin: '0 0 10px',
                        color: 'var(--ink)', lineHeight: 1.3,
                      }}>
                        {article.title}
                      </h2>
                      <p style={{
                        margin: 0, color: 'var(--ink-muted)',
                        fontSize: '0.95rem', lineHeight: 1.6,
                      }}>
                        {article.description}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
